import { AxiosService } from 'src/axios/axios.service';
import { mapCreditsServiceError } from 'src/pages/WhatsappApi/Credits.service.ts';
import {
  tOneTimeCreditPurchasePayload,
  tOneTimeCreditPurchaseResponse,
  tPaymentMethodOption,
} from './PurchaseCreditsModal.types.ts';

type tGetPaymentMethodsResponse = {
  data: {
    paymentMethods: tPaymentMethodOption[];
    defaultPaymentMethodId?: string;
  };
};

/**
 * Saved cards plus the account's default payment method id. The default is kept
 * (not flattened away) so the purchase flow can preselect the customer's
 * default card instead of whichever card happens to be first in the list.
 */
export type tGetPaymentMethodsResult = {
  paymentMethods: tPaymentMethodOption[];
  defaultPaymentMethodId: string | null;
};

export const OneTimeCreditsService = {
  /**
   * POST /workspace/:wid/addons/one-time-credits — BFF.
   *
   * Pre-RET-1066 this called leadbilling directly. The BFF now proxies the
   * request so the front no longer reaches the billing service over its own
   * client (consistent with the rest of the addons/* and credits/* routes).
   * The wire body is unchanged — leadbilling still validates the payload.
   *
   * GRO-811 Phase 2.5: payload includes optional `package_id`, `custom_credits`,
   * and `metadata` fields (additive — the legacy modal keeps sending only
   * `price_id` + `payment_method_id`).
   *
   * GRO-811 Phase 2.4: errors flow through `mapCreditsServiceError` — see its
   * docblock in `Credits.service.ts` for the status-code table. Deliberately
   * not restated here: this enumeration used to be maintained by hand in three
   * files and the copies drifted the first time a status branch was added.
   */
  purchaseCredits: async (
    workspaceUuid: string,
    payload: tOneTimeCreditPurchasePayload,
  ): Promise<tOneTimeCreditPurchaseResponse> => {
    const config = { isBFFService: true, headers: {} };
    try {
      return await AxiosService.post<
        tOneTimeCreditPurchasePayload,
        tOneTimeCreditPurchaseResponse
      >(
        `/workspace/${workspaceUuid}/addons/one-time-credits`,
        payload,
        config,
      );
    } catch (rawError) {
      mapCreditsServiceError(rawError);
      throw rawError; // unreachable — mapCreditsServiceError always throws.
    }
  },

  /**
   * GET /workspace/:wid/payment-methods — BFF.
   *
   * Pre-RET-1066 this called leadbilling directly. The BFF now proxies the
   * request (same shape pass-through: `data.paymentMethods` + optional
   * `defaultPaymentMethodId`) so the front no longer reaches the billing
   * service over its own client, matching the rest of the addons/* and
   * credits/* routes already migrated.
   *
   * Returns the default payment method id alongside the list so the caller can
   * surface the customer's default card in the purchase flow.
   */
  getPaymentMethods: async (
    workspaceId: string,
  ): Promise<tGetPaymentMethodsResult> => {
    const config = { isBFFService: true, headers: {} };
    const response = await AxiosService.get<tGetPaymentMethodsResponse>(
      `/workspace/${workspaceId}/payment-methods`,
      config,
    );
    return {
      paymentMethods: response.data.paymentMethods ?? [],
      defaultPaymentMethodId: response.data.defaultPaymentMethodId ?? null,
    };
  },
};
